/* eslint-disable no-shadow */
import * as React from 'react';
import { UserData } from '@taxdown/kami-sama/dist/definition';
import { FRONT_PLANS } from 'Constants/plans';

declare global {
  interface Window {
    amplitude: {
      getInstance: () => {
        options: {
          deviceId: string;
        };
        getSessionId: () => number;
        onInit: (params: unknown) => void;
      };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any;
    _cio?: {
      identify: (data: { id: string; email: string }) => void;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hj: any;
    _hjSettings: { hjid: number; hjsv: number };
    Landbot: {
      hasIntercom: boolean;
      hasSatId: boolean;
      Container: (args) => void;
    };
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'kueskipay-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

export type Nullable<T> = T | null;

export type Undefinedable<T> = T | undefined;

export type ActionType<T> = {
  type: string;
  payload?: T;
};

export type ReducerType = {
  [key: string]: null | [] | Record<string, never>;
};

export type StateType<T> = {
  [key: string]: T;
};

export type ItemDataType<T> = {
  item: T;
};

export type ResponseType<T> = {
  data: ItemDataType<T>;
};

export type ResponseArrayType<T> = {
  data: ItemDataType<T>[];
};

export type ErrorResponseType = {
  response: {
    data: { code: string; status: number; detail: string; title: string };
  };
};

// eslint-disable-next-line no-shadow
export enum ResultTypes {
  toReturn = 'toReturn',
  toPay = 'toPay',
  equal = 'equal',
  zero = 'zero',
}

export enum FilingStatus {
  pending = 'pending',
  filed = 'filed',
  resolved = 'resolved',
  unresolved = 'unresolved',
  deposited = 'deposited',
}
export type RecordFilingInfo = {
  amount: number;
  taxReturnType: ResultTypes;
  status: FilingStatus;
  order: number;
  isActive: boolean;
  resolvedAmount?: number;
  deposittedAmount?: number;
  filedAt?: string;
  resolvedAt?: string;
  deposittedAt?: string;
  clabeAccount?: string;
  isPartial?: boolean;
  taxAgencyResolvedAmount?: number;
  taxAgencyResolvedAt?: Date;
  created?: string;
};

export type FilingInfo = {
  count?: number;
  recent: RecordFilingInfo;
  filingInfos: RecordFilingInfo[];
  isPartial?: boolean;
};

export type SeasonType = {
  enabled: boolean;
  isSeasonOver: boolean;
  daysLeft: number;
  seasonEnd: string;
  seasonStart: string;
  taxYear: string;
  recordId: string;
  stage: string;
  filingInfo?: FilingInfo;
  records: { id: string; stage: string; pdf?: string }[] | unknown[];
};

export enum taxReturnFilingType {
  complementary = 'complementary',
  simple = 'simple',
}

export type WarningsType = {
  id: string;
  value: number;
  message: string;
}[];

export type ResultType = {
  mandatory: boolean;
  taxReturnType: string;
  taxReturnSubType: string;
  taxReturnResult: number;
  warnings: WarningsType;
  isRefundable?: WarningsType;
  instantRefundRequest?: boolean;
  taxReturnFilingType?: taxReturnFilingType;
  isUserForFed?: boolean;
  needEfirmaUser?: boolean;
  recordId?: string;
  flexUser?: boolean;
  flexToPro?: boolean;
};

export type SeasonFiltered = {
  recordId: string;
  stage: string;
  enabled: boolean;
  isSeasonOver: boolean;
  daysLeft: number;
  seasonEnd: string;
  seasonStart: string;
  taxYear: string;
  filingInfo?: {
    count?: number;
    recent: RecordFilingInfo;
    filingInfos: RecordFilingInfo[];
    isPartial?: boolean;
    filedTimes?: number;
  };
  isUserWithPreFiling?: boolean;
  taxReturnResult?: ResultType;
  pdf?: string;
};
export type InstantRefundType = {
  instantRefundId: string;
  userId: string;
  taxReturnResult: {
    result: number;
    returnType: string;
  };
  status: string;
  taxYear: string;
};

export type ScreenPropsType = {
  screenName: string;
};

export type TaxScreenType = {
  tax: SeasonFiltered;
  pdf?: string;
};

export type FiledScreenProps = {
  tax: SeasonFiltered;
  handleShowDraft: (data: string) => void;
};

// eslint-disable-next-line no-shadow
export enum ScreenTypes {
  START = 'start',
  EDIT = 'edit',
  CHILD_ADD = 'child_add',
  SUMMARY = 'summary',
  SUMMARY_ADD = 'summary_add',
  SUBSUMMARY_FILTERED = 'subsummary_filtered',
  SUMMARY_CLASIFICATOR = 'summary_clasificator',
  CHILD = 'child',
  END = 'end',
}

export type SaveDataType = { stage: string; data?: UserData; next?: boolean };

export type TaxType = { recordId: string; taxYear: string | number };

export type TaxSelectedType = null | TaxType;

export type RecordType = {
  recordId: string;
  userId: string;
  taxYear: string;
  taxDataId: string;
  stage: string;
  data: UserData;
  created: Date;
  modified: Date;
};

export enum VerifyRfcMethod {
  MOBILE = 'mobile',
  SINGLE_SIGN_ON = 'sso',
  WEB_FEDERATION = 'wsfed',
}

export type RfcUserType = {
  rfcAndPassword: { rfc: string; password: string; verificationMethod?: VerifyRfcMethod };
};

export type EfirmaType = {
  certificate: string;
  privateKey: string;
  passphrase: string;
};

export type EfirmaUserType = {
  efirma: EfirmaType;
};

export type SatUserType = RfcUserType | EfirmaUserType;

export type DeleteUserType = {
  userId: string;
  rfc: string;
  password: string;
  stage: string;
  created: string;
  modified: string;
};

export type DeleteUserIsAllowed = {
  isDeleteAllowed: boolean;
};

export type SubmitInfoFilingType = {
  CLABE: string;
  prefilledCLABE?: string;
  isPartial: boolean;
  installmentsList: number[][];
  installmentsListSelected?: number[][];
  installments: number;
  payWithExternalService?: boolean;
};

export type SubmitResultType = {
  recordId: string;
  userId: string;
  taxYear: string;
  taxDataId: string;
  stage: string;
  data: UserData;
  outputs: object;
  result: ResultType;
  submitInfo: SubmitInfoFilingType;
  created: string;
  modified: string;
};

export type PlansTypePrimitives = {
  id: string;
  name: string;
  description: string;
  image: string;
  type: string;
  price: {
    currency: string;
    amount: number;
    id: string;
    amountDecimal: string;
  };
  priceUser: {
    currency: string;
    amount: number;
    id: string;
    amountDecimal: string;
  };
  service?: string;
  active: boolean;
  available: boolean;
  subscribed: boolean;
  pendingAmount?: number;
  subscribedFull?: boolean;
  checked: boolean;
  bpmLabel: string;
};

export type PlansType = Omit<PlansTypePrimitives, 'name'> & {
  name: FRONT_PLANS;
};

export type UserDataType = {
  email: string;
  newUser: boolean;
  phone: string;
  userId: string;
  status: string;
};

export type ConsultantType = {
  id: number;
  name: string;
  img: string;
  gender: number;
};

export type CustomerType = {
  id: string;
  plans: {
    planId: string;
    name: string;
    started: string;
  }[];
  created?: string;
  activePlan: {
    name: string;
    planId: string;
    started: string;
  };
  phone?: string;
  email?: string;
  name?: string;
  lastName?: string;
  address: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  oneTimeProducts: {
    amountPaid: number;
    paymentMethod: string;
    productId: string;
    productType: string;
    purchasedAt: string;
  }[];
  userId: string;
  isAutoRenewalEnabled?: boolean;
};

export type CheckoutType = {
  sessionUrl: string;
};

export type ShippingType = {
  name: string;
  lastName: string;
  phone?: string;
  email?: string;
  address: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
};

export type NotificationsPreferenceType = {
  marketingEmail: boolean;
  marketingPush: boolean;
  marketingSms: boolean;
  taxEmail: boolean;
  taxPush: boolean;
  taxSms: boolean;
};

export type FormSubmitted = {
  recordId?: string;
  screenId?: string;
  values?: unknown;
};

export type UserType = {
  email: string;
  newUser: boolean;
  phone: string;
  userId: string;
  status: string;
  partner: string;
  personalInfo?: {
    firstLastName: string;
    name: string;
    secondLastName: string;
  };
};

export enum Providers {
  GOOGLE = 'Google',
  TAXDOWN = 'Taxdown',
  APPLE = 'SignInWithApple',
}

export enum SocialProviders {
  GOOGLE = 'Google',
  FACEBOOK = 'Facebook',
}

export type AuthValuesType = {
  id: string;
  value: { value?: string; isValid?: boolean };
};

export enum ReactNativeMessageTypes {
  analyticsIdentify = 'analyticsIdentify',
  analyticsReset = 'analyticsReset',
  openLink = 'openLink',
  openRating = 'openRating',
  openLinkExternal = 'openLinkExternal',
  writeToDisk = 'writeToDisk',
}

export type FeedBackType = {
  taxYear: string;
  rating: number;
  comment: string;
};

export type ReferralLinksType = {
  userId: string;
  email: string;
  code: string;
  created: string;
  verified: boolean;
  links: {
    channel: string;
    title: string;
    url: string;
  }[];
  clabe?: string;
  fullName?: string;
  amountByReferred?: number;
  maxProfitThreshold?: number;
  totalEarns?: number;
  referredAmount: number;
  referrerAmount: number;
};

export enum ReferralShareMode {
  socialShareLinks = 'socialShareLinks',
  linksSubmit = 'linksSubmit',
  linksCalculate = 'linksCalculate',
}

export enum BoxModes {
  notStarted = 'notStarted',
  lastStep = 'lastStep',
  paid = 'paid',
}

export type Profit = {
  names: string[];
  total: number;
};

export type Profits = {
  lastStep: Profit;
  notStarted: Profit;
  paid: Profit;
};

export enum ABPhoneFlagsValue {
  DG = 'dg',
  INFO = 'info',
  SIGNUP = 'signup',
}

export enum FlagsKey {
  EFIRMA_DG_ENABLED = 'efirma_dg_enabled',
  RESULT_MANDATORY_UPSELLING = 'result_mandatory_upselling',
  FLOW_ENABLED = 'flow_enabled',
  FILE_NOW_PAY_LATER = 'file_now_pay_later',
  FILE_NOW_PAY_LATER_SETUP_CARD = 'file_now_pay_later_setup_card',
  MGM_FLOW_ENABLED = 'mgm_flow_enabled',
  PHONE_REQUEST_FLOW = 'phone_request_flow',
  INSTANT_REFUND_ENABLED = 'instant_refund_enabled',
}

export type PatchClabe = {
  clabe: string;
  fullName: string;
};

export enum QueryKeys {
  FETCH_PREVIOUS_YEARS = 'fetchPreviousYearsUser',
  FETCH_PREVIOUS_YEARS_RESULT = 'fetchPreviousYearsResult',
  FETCH_FILING_USERS = 'fetchFilingUser',
  FETCH_SEASONS = 'fetchSeasons',
  FETCH_NEW_SEASONS = 'fetchNewSeasons',
  FETCH_GET_CUSTOMER = 'useFetchGetCustomers',
  CONFIRM_DECLARATION = 'confirmDeclaration',
  FETCH_PREVIOUS_YEARS_REQUEST = 'fetchPreviousYearsRequest',
  SAVE_CLABE_MUTATION = 'save_clabe_mutation',
  GET_REFERRAL_LINKS = 'get_referral_links',
  DELETE_USER_KEY = 'deleteUserKey',
  FETCH_GET_DELETE_USERS = 'fetch_get_delete_user',
  FETCH_GET_PREFERENCES = 'fetch_get_preference',
  UPDATE_NOTIFICATIONS_PREFERENCE = 'update_notifications_preference',
  ANUAL_DECLARATION = 'anual_declaration',
  ANUAL_DECLARATION_QUIZ = 'anual_declaration_quiz',
  PREVIOUS_YEARS_QUIZ = 'previous_years_quiz',
  PREVIOUS_YEARS = 'previous_years',
  FETCH_SAT_STAGE_USER = 'fetchSatStageUser',
  SAVE_SAT_USER = 'saveSatUser',
  VALIDATE_CREDENTIALS = 'validateCrendentials',
  FETCH_SAT_EFIRMA_USER = 'fetchSatEfirmaUser',
  FETCH_GET_FEATURE_FLAG = 'fetchGetFeatureFlag',
  RETRY_SAT_MUTATION = 'retrySatMutation',
  REQUEST_PREVIOUS_YEARS = 'fetchRequestPreviousYears',
  SUBMIT_DECLARATION = 'SUBMIT_DECLARATION',
  PATCH_FILING_USERS = 'PATCH_FILING_USERS',
  PATCH_CLABE_USERS = 'PATCH_CLABE_USERS',
  SAVE_PHONE_KEY = 'save_phone_key',
  FETCH_GET_PLANS = 'useFetchGetPlans',
  CHEKOUT_SESSION = 'checkoutSession',
  FETCH_GET_CUSTOMERS = 'useFetchGetCustomers',
  CHECKOUT_PREVIOUS_YEARS = 'checkoutPreviousYears',
  CALCULATE_RESULT = 'calculateResult',
  CALCULATE_PRICE = 'calculatePrice',
  USER_SAT_INFO = 'userSatInfo',
  GET_PREVIOUS_YEAR = 'getPreviousYear',
  GET_PREVIOUS_YEAR_PERCENTAGE = 'getPreviousYearPercentage',
  GET_PROFITS = 'getProfits',
  GET_LINKS = 'getLinks',
  SAVE_USER_PHONE = 'save_user_phone',
  FETCH_GET_RESULT = 'fetchGetResult',
  SAVE_PAY_LATER = 'savePayLater',
  GET_PREVIOUS_YEAR_RESULT = 'getPreviousYearResult',
  SAVE_INSTANT_REFUND = 'saveInstantRefund',
  GET_INSTANT_REFUND_REQUEST = 'getInstantRefundRequest',
  GET_PLAN_FLEX_INFO = 'getPlanFlexInfo',
  GET_PLAN_FLEX_INVOICE = 'getPlanFlexInvoice',
  GET_USER_COMPLEX = 'getUserComplex',
  SUBMIT_ANUAL_DECLARATION = 'submitAnualDeclaration',
  FETCH_PREVIOUS_YEARS_ANUAL_DECLARATION = 'fetchPreviousYearsAnualDeclaration',
  FETCH_GET_CUSOMERS = 'fetchGetCustomers',
  SUBSCRIBE_FLEX_LITE_CHECKOUT = 'subscribeFlexLiteCheckout',
  SUBMIT_ANUAL_DECLARATION_CHECKOUT = 'submitAnualDeclarationCheckout',
  FETCH_GET_RESULT_XML = 'fetchGetResultXml',
  SAVE_PAY_LATER_XML = 'savePayLaterXml',
  GET_PREVIOUS_YEAR_RESULT_XML = 'getPreviousYearResultXml',
  SAVE_INSTANT_REFUND_XML = 'saveInstantRefundXml',
  GET_INSTANT_REFUND_REQUEST_XML = 'getInstantRefundRequestXml',
  GET_PLAN_FLEX_INFO_XML = 'getPlanFlexInfoXml',
  GET_CLABE_XML = 'getClabeXml',
  SUBMITTED_USER_XML = 'submitterUserXml',
  GET_FORMS = 'GET_FORMS',
  GET_USER_DATA = 'GET_USER_DATA',
  SAVE_USER_DATA = 'SAVE_USER_DATA',
  RESET_USER = 'RESET_USER',
  RESTART_USER = 'RESTART_USER',
  GET_USER_STAGE = 'GET_USER_STAGE',
  GET_RECORD = 'GET_RECORD',
  GET_CUSTOMER_INFO = 'useFetchGetCustomers',
  SAVE_REVIEW = 'save_review',
  SAVE_SAT_PASSWORD = 'save_sat_password',
  CALCULATING_LOADER = 'calculatingLoader',
  GET_SPEI_OUTS = 'getSpeiOuts',
  SUBSCRIBE_FLEX_LITE = 'subscribeFlexLite',
  AUTO_RENEWAL = 'autoRenewal',
  SUBMIT_DECLARATION_CROSS_SELL = 'submitDeclarationCrossSell',
  FETCH_SUCCESS_RESULT = 'fetchSuccessResult',
  REINTENTS = 'reintents',
  SAVE_DASHBOARD_PHONE = 'save_dashboard_phone',
  CALCULATE_RESULT_PREVIOUS_YEAR = 'calculateResultPreviousYear',
  PREVIOUS_YEARS_IS_REQUEST = 'previousYearsIsRequest',
  REQUEST_PREVIOUS_YEAR = 'requestPreviousYear',
  GET_RECORD_XML = 'GET_RECORD_XML',
  GET_CEP = 'getCep',
  FETCH_GET_XML_RECORD = 'fetchGetXmlRecord',
  FETCH_GET_RECORD_RESULT_XML = 'fetchGetRecordResultXml',
  VALIDATE_CLABE = 'validateClabe',
  REQUEST_PREVIOUS_YEARS_INITIAL = 'requestPreviousYearsInitial',
  SUBSCRIBE_FLEX = 'subscribeFlex',
  RENEWAL_FLEX = 'renewalFlex',
  CREATE_DOMICILIATION = 'createDomiciliation',
  GET_USER_AUTORIZED_DOMICILIATIONS = 'getUserAutorizedDomiciliations',
  GET_USER_CHARGEMENTS = 'getUserChargements',
}

export type IsPreviousYear = {
  products: {
    isPreviousYear: boolean;
    request?: boolean;
  };
};

export type PreviousYearsPercentage = {
  name: string;
  productType: string;
  discountedPrice: number;
  price: number;
  productId: string;
};

export enum TaxProfile {
  SIMPLE = 'simple',
  COMPLEX = 'complex',
}
export type UserComplexResponse = { userId: string; taxProfile: TaxProfile };

export type savePayLaterResponse = {
  status: number;
};

export type invoiceFlexResponse = {
  amountDue: number;
  invoiceId: string;
  status: string;
  baseServiceAmount: number;
};

export type FlexInfoResponse = {
  discountedPrice: number;
  price: number;
  productId: string;
  discount?: {
    valid: boolean;
    amountOff: number;
    percentOff: number;
  };
};

export type RecordArchiveFlowResponse = {
  flowActive: boolean;
  ready?: boolean;
};

export type RecordArchiveResponse = {
  id: string;
  userId: string;
  taxYear: string;
  satInfo: {
    satInfoResult: {
      type: string;
      amount: number;
    };
    isFiled: boolean;
    isComplementary: boolean;
    subregimenesIds: string[];
    subregimenesNames: string[];
    clabe?: string;
  };
  statusSummary: {
    lastStatus: string;
    amountTotal: number;
    amountResolved: number;
    isRecoverable: boolean;
    isFiled: boolean;
    isComplementary: boolean;
    subregimenesIds: string[];
    subregimenesNames: string[];
    hasRefundsRejected: boolean;
    hasRefunds: boolean;
  };
};

export type SpeiOutResponse = {
  userId: string;
  destinationRfc: string;
  destinationName: string;
  created: Date;
  status: string;
  trackId: string;
  concept: string;
  amountCentavos: number;
  amountDecimal: number;
};

export type CepResponse = {
  userId: string;
  trackId: string;
  amount: number;
  appliedAt: string;
  fileUrl: string;
  from: string;
  to: string;
  fileBase64?: string;
  kind: string;
};

export type ValidateClabeResponse = {
  clabeAccount: string;
  bankName: string;
  bankTag: string;
  bankCode: string;
  isFormatValid: boolean;
  isPayLaterAllowed: boolean;
  isBlackListed: boolean;
  formatError: string | null;
};

export interface ReintentType {
  id: string;
  userId: string;
  taxYear: string;
  executedAttempts: number;
  lastAttemptExecutedOn: string;
  nextAttemptOn: string;
  latestStatusReported: string;
  isLongRunning: boolean;
}

export type CreateDomiciliationResponse = {
  id: string;
  userId: string;
  email: string;
  activationMethod: string;
  status: string;
  activationLink: string;
};

export type UserChargement = {
  id: string;
  debtId: string;
  userId: string;
  amount: number;
  chargementMethod: string;
  status: string;
  created: string;
  modified: string;
  manualPaymentLink?: string;
};

export type UserChargementsResponse = {
  debt: {
    id: string;
    userId: string;
    taxReturnId: string;
    taxReturnFilingId: string;
    invoiceId: string;
    status: string;
    dueAmount: number;
    receivedAmount: number;
    receivedPaymentAt: string;
    paymentRequestedCount: number;
    amountPaid: number;
    paymentMethod: string;
    created: string;
    modified: string;
  };
  chargements: UserChargement[];
  totalChargedAmount: number;
  remainingDebtAmount: number;
  isFullyCharged: boolean;
  latestChargement: UserChargement;
  chargementsCount: number;
  actionStatus: string;
};

export type UserAutorizedDomiciliationsResponse = {
  entityId: string;
  userId: string;
  type: string;
  isActive: boolean;
  info: {
    brand: string;
    lastDigits: string;
    expirationDate: string;
  };
};
